import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Download, Eye, Trash2, Bell, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SavedQuotes = ({ onBack, onLoadQuote, user }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [renderError, setRenderError] = useState(null);
  const [requestingUpdate, setRequestingUpdate] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/configurations`, {
        headers: getAuthHeaders()
      });
      // Ensure data is an array and filter out any invalid entries
      const data = Array.isArray(response.data) ? response.data : [];
      
      // More thorough validation and sanitization
      const validQuotes = data.filter(q => {
        try {
          return q && 
                 typeof q === 'object' && 
                 q.id && 
                 typeof q.id === 'string';
        } catch (e) {
          console.error('Invalid quote object:', q, e);
          return false;
        }
      }).map(q => ({
        ...q,
        // Ensure critical fields have safe defaults
        project_name: q.project_name || 'Untitled Project',
        status: q.status || 'active',
        banks: Array.isArray(q.banks) ? q.banks : [],
        updated_at: q.updated_at || new Date().toISOString()
      }));
      
      console.log('Fetched quotes:', validQuotes.length);
      setQuotes(validQuotes);
      setRenderError(null);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setMessage('Error loading quotes. Please refresh the page.');
      setQuotes([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuotes.length === 0) return;
    
    const count = selectedQuotes.length;
    if (!window.confirm(`Delete ${count} selected quote${count > 1 ? 's' : ''}?`)) return;
    
    setDeleting(true);
    setMessage("");
    
    try {
      await Promise.all(
        selectedQuotes.map(id =>
          axios.delete(`${API}/configurations/${id}`, {
            headers: getAuthHeaders()
          })
        )
      );
      
      // Filter out deleted quotes safely
      setQuotes(prevQuotes => prevQuotes.filter(q => q && q.id && !selectedQuotes.includes(q.id)));
      setSelectedQuotes([]);
      setMessage(`Successfully deleted ${count} quote${count > 1 ? 's' : ''}`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error('Error deleting quotes:', err);
      setMessage("Error deleting some quotes. Please refresh the page.");
      // Refresh the list on error
      setTimeout(() => fetchQuotes(), 2000);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectQuote = (id) => {
    setSelectedQuotes(prev =>
      prev.includes(id)
        ? prev.filter(qId => qId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedQuotes.length === quotes.length && quotes.length > 0) {
      setSelectedQuotes([]);
    } else {
      // Only map quotes that have valid IDs
      setSelectedQuotes(quotes.filter(q => q && q.id).map(q => q.id));
    }
  };

  const handleRequestUpdate = async (quote) => {
    if (!window.confirm(`Request update from ${quote.user_email} for "${quote.project_name}"?`)) {
      return;
    }
    
    setRequestingUpdate(quote.id);
    try {
      await axios.post(
        `${API}/configurations/${quote.id}/request-update`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setMessage(`✅ Update request sent to ${quote.user_email}`);
      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      console.error('Error requesting update:', err);
      setMessage(err.response?.data?.detail || "Failed to send update request");
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setRequestingUpdate(null);
    }
  };

  const handleDownloadPDF = async (configId) => {
    try {
      const response = await axios.get(
        `${API}/configurations/${configId}/download-pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${configId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-blue-100 text-blue-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.active;
  };

  // Wrap entire render in try-catch
  try {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Saved Quotes ({quotes.length})</CardTitle>
              {quotes.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all"
                      checked={selectedQuotes.length === quotes.length && quotes.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all" className="text-sm text-slate-600 cursor-pointer">
                      Select All
                    </label>
                  </div>
                  {selectedQuotes.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleBulkDelete}
                      disabled={deleting}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Selected ({selectedQuotes.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {message && (
              <Alert className="mb-4 bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">{message}</AlertDescription>
              </Alert>
            )}
            
            {loading ? (
              <p className="text-center text-slate-500">Loading...</p>
            ) : quotes.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No saved quotes yet</p>
            ) : renderError ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-2">Error displaying quotes</p>
                <p className="text-sm text-slate-500 mb-4">{renderError}</p>
                <Button onClick={() => { setRenderError(null); fetchQuotes(); }}>
                  Retry
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {quotes.map((quote) => {
                  try {
                    // Safely extract values with defaults
                    const projectName = quote?.project_name || 'Untitled Project';
                    const status = quote?.status || 'active';
                    const banksCount = Array.isArray(quote?.banks) ? quote.banks.length : 0;
                    const updatedAt = quote?.updated_at ? new Date(quote.updated_at).toLocaleDateString('en-GB') : 'N/A';
                    const finalTotal = quote?.final_total;
                    
                    return (
                      <div
                        key={quote.id || Math.random()}
                      className={`border rounded-lg p-4 transition-colors ${
                        selectedQuotes.includes(quote.id) ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="pt-1">
                          <Checkbox
                            id={`quote-${quote.id}`}
                            checked={selectedQuotes.includes(quote.id)}
                            onCheckedChange={() => toggleSelectQuote(quote.id)}
                          />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-slate-900">
                              {projectName}
                            </h3>
                            <Badge className={getStatusBadge(status)}>
                              {status}
                            </Badge>
                            {quote?.quote_reference && (
                              <Badge variant="outline" className="text-slate-600">
                                Ref: {quote.quote_reference}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            {quote?.configurator_type === 'parking_barrier' ? (
                              <p>Parking Barriers · {quote?.lanes?.length || 0} lane(s) · Updated {updatedAt}</p>
                            ) : quote?.configurator_type === 'full_height_turnstiles' ? (
                              <p>Full Height Turnstiles · {quote?.turnstile_type} · {quote?.arm_config} arms · {quote?.cover_type} · Qty: {quote?.quantity || 1} · Updated {updatedAt}</p>
                            ) : quote?.configurator_type === 'atlas' ? (
                              <p>Atlas Access Control · {quote?.total_doors || 0} doors · {quote?.total_controllers || 0} controllers · Updated {updatedAt}</p>
                            ) : (
                              <p>{banksCount} bank(s) · Comet Speedlane · Updated {updatedAt}</p>
                            )}
                            {finalTotal && typeof finalTotal === 'number' && (
                              <p className="font-medium text-slate-900">£{finalTotal.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingQuote(quote)}
                            title="View Details"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onLoadQuote(quote)}
                            title="Load into Configurator"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {quote?.pdf_path && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadPDF(quote.id)}
                              className="text-blue-600"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {user?.role === 'distributor_admin' && quote.user_email !== user?.email && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-300 text-orange-700 hover:bg-orange-50"
                              onClick={() => handleRequestUpdate(quote)}
                              disabled={requestingUpdate === quote.id}
                              title="Request Update"
                            >
                              <Bell className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  } catch (err) {
                    console.error('Error rendering quote:', quote, err);
                    // Skip this quote and continue
                    return null;
                  }
                }).filter(Boolean)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quote Details Modal */}
        {viewingQuote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{viewingQuote.project_name || 'Quote Details'}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4"
                  onClick={() => setViewingQuote(null)}
                >
                  ✕
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Quote Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500">User</p>
                      <p className="text-sm font-medium">{viewingQuote.user_email}</p>
                    </div>
                    {viewingQuote.quote_reference && (
                      <div>
                        <p className="text-xs text-slate-500">Quote Ref</p>
                        <p className="text-sm font-medium">{viewingQuote.quote_reference}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Category</p>
                      <p className="text-sm font-medium capitalize">
                        {viewingQuote.configurator_type === 'parking_barrier' ? 'Parking Barriers' :
                         viewingQuote.configurator_type === 'full_height_turnstiles' ? 'Full Height Turnstiles' :
                         viewingQuote.configurator_type === 'atlas' ? 'Atlas Access Control' :
                         'Comet Speedlane'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Value</p>
                      <p className="text-sm font-bold text-green-600">£{viewingQuote.final_total?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Created</p>
                      <p className="text-sm">{viewingQuote.created_at ? new Date(viewingQuote.created_at).toLocaleDateString('en-GB') : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <Badge className={
                        viewingQuote.status === 'won' ? 'bg-green-100 text-green-800' :
                        viewingQuote.status === 'lost' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }>
                        {viewingQuote.status || 'active'}
                      </Badge>
                    </div>
                  </div>

                  {/* Bill of Materials */}
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Bill of Materials</h3>
                    {(viewingQuote.combined_bom || viewingQuote.bom)?.length > 0 ? (
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold">SKU</th>
                              <th className="text-left py-2 px-3 font-semibold">Description</th>
                              <th className="text-center py-2 px-3 font-semibold">Qty</th>
                              <th className="text-right py-2 px-3 font-semibold">Unit Price</th>
                              <th className="text-right py-2 px-3 font-semibold">Line Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(viewingQuote.combined_bom || viewingQuote.bom).map((item, idx) => (
                              <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs">{item.sku}</td>
                                <td className="py-2 px-3">{item.description}</td>
                                <td className="py-2 px-3 text-center">{item.quantity}</td>
                                <td className="py-2 px-3 text-right">£{item.unit_price?.toFixed(2)}</td>
                                <td className="py-2 px-3 text-right font-medium">£{item.line_total?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 font-semibold">
                            <tr>
                              <td colSpan="4" className="py-2 px-3 text-right">Subtotal:</td>
                              <td className="py-2 px-3 text-right">
                                £{(viewingQuote.combined_bom || viewingQuote.bom)
                                  .reduce((sum, item) => sum + (item.line_total || 0), 0)
                                  .toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-lg">
                        <p>No BOM details available for this quote.</p>
                        <p className="text-xs mt-1">BOM data is saved for quotes created after the system update.</p>
                      </div>
                    )}
                  </div>

                  {/* Configuration Summary */}
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3">Configuration Summary</h3>
                    <div className="text-sm bg-slate-50 p-4 rounded-lg">
                      {viewingQuote.configurator_type === 'parking_barrier' && viewingQuote.lanes && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Lanes:</span> {viewingQuote.lanes.length}</p>
                          {viewingQuote.lanes.length > 0 && (
                            <>
                              <p><span className="font-medium">Chassis:</span> {viewingQuote.lanes[0].chassisType}</p>
                              <p><span className="font-medium">Boom:</span> {viewingQuote.lanes[0].boomLength}m</p>
                              <p><span className="font-medium">LED:</span> {viewingQuote.lanes[0].hasLED ? 'Yes' : 'No'}</p>
                            </>
                          )}
                        </div>
                      )}
                      {viewingQuote.configurator_type === 'full_height_turnstiles' && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Type:</span> {viewingQuote.turnstile_type}</p>
                          <p><span className="font-medium">Arms:</span> {viewingQuote.arm_config}</p>
                          <p><span className="font-medium">Cover:</span> {viewingQuote.cover_type}</p>
                          <p><span className="font-medium">Quantity:</span> {viewingQuote.quantity}</p>
                        </div>
                      )}
                      {viewingQuote.banks && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Banks:</span> {viewingQuote.banks.length}</p>
                          {viewingQuote.banks.map((bank, idx) => (
                            <p key={idx} className="ml-4">
                              Bank {bank.bank_number}: {bank.total_lanes} lanes ({bank.dda_lanes} DDA)
                            </p>
                          ))}
                        </div>
                      )}
                      {viewingQuote.total_doors && (
                        <div className="space-y-1">
                          <p><span className="font-medium">Doors:</span> {viewingQuote.total_doors}</p>
                          <p><span className="font-medium">Controllers:</span> {viewingQuote.total_controllers}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        onLoadQuote(viewingQuote);
                        setViewingQuote(null);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Load into Configurator
                    </Button>
                    {viewingQuote.pdf_path && (
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadPDF(viewingQuote.id)}
                        className="text-blue-600"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
    );
  } catch (err) {
    console.error('SavedQuotes render error:', err);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Error Loading Saved Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  An error occurred while displaying your saved projects. 
                  Please try refreshing the page or contact support if the problem persists.
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <p className="text-sm text-slate-600">Error details:</p>
                <pre className="text-xs bg-slate-100 p-3 rounded overflow-auto">
                  {err.toString()}
                </pre>
              </div>
              <div className="mt-4 space-x-2">
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
                <Button variant="outline" onClick={onBack}>
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
};

export default SavedQuotes;
